import React, { useState, useEffect, useRef, useCallback } from 'react';
import classNames from 'classnames';
import { FormattedMessage } from '../../../util/reactIntl';
import { createResourceLocatorString } from '../../../util/routes';
import { recordBehaviorEvent } from '../../../util/api';
import CATEGORY_THUMBNAILS, { ICON_THUMBNAIL_IDS } from './categoryThumbnails';
import { DESIGN_SHOWCASE_ENABLED } from '../../../config/configFeatures';
import css from './SearchSubNav.module.css';

const TOP_CATEGORIES = [
  { id: 'materials', label: 'Materials' },
  { id: 'ffe', label: 'FF&E' },
  { id: 'architectural', label: 'Architectural' },
];

// Hidden from the nav for now — flip back to true to restore the What's New
// tile (L1 picker) and tab (category tab bar). The route and page underneath
// are untouched; this only removes the two entry points here.
const SHOW_WHATS_NEW_NAV = false;

// Placeholder colours per sub-category — act as a fallback when a tile has
// no entry in CATEGORY_THUMBNAILS. Exported so TopbarMobileMenu can reuse
// the same gradient palette for its mobile tile grid.
export const CATEGORY_COLORS = {
  // L1 (top-level) — the three L1 heroes are transparent-background PNGs, so
  // they get no plate behind them; the tile surface shows through instead.
  materials: 'transparent',
  ffe: 'transparent',
  architectural: 'transparent',
  // Materials
  stone: 'linear-gradient(135deg, #c9c2b8 0%, #e8e2da 100%)',
  'masonry-brick': 'linear-gradient(135deg, #b5694a 0%, #d4906e 100%)',
  'ceramic-tile': 'linear-gradient(135deg, #d0d8e0 0%, #eef1f5 100%)',
  wood: 'linear-gradient(135deg, #9b6f3e 0%, #c49a6c 100%)',
  metal: 'linear-gradient(135deg, #8c9aa8 0%, #b8c4cc 100%)',
  glass: 'linear-gradient(135deg, #a8c8d8 0%, #d4eaf0 100%)',
  concrete: 'linear-gradient(135deg, #8a8e8f 0%, #b8bcbd 100%)',
  textile: 'linear-gradient(135deg, #c4a882 0%, #dfc9aa 100%)',
  wallcovering: 'linear-gradient(135deg, #b8a8c0 0%, #d8ccdf 100%)',
  'carpet-rug': 'linear-gradient(135deg, #9e8c7a 0%, #c4b4a0 100%)',
  'resilient-flooring': 'linear-gradient(135deg, #7a9e8c 0%, #a8c4b8 100%)',
  leather: 'linear-gradient(135deg, #8c6848 0%, #b08860 100%)',
  'paint-finish': 'linear-gradient(135deg, #d8c8e0 0%, #ece4f0 100%)',
  'tropical-natural': 'linear-gradient(135deg, #6e9e70 0%, #9ac49c 100%)',
  // FF&E
  furniture: 'linear-gradient(135deg, #8a7868 0%, #b4a090 100%)',
  sanitaryware: 'linear-gradient(135deg, #c8d0d8 0%, #e4eaee 100%)',
  'tapware-fittings': 'linear-gradient(135deg, #a0aab0 0%, #c8d0d8 100%)',
  lighting: 'linear-gradient(135deg, #d4c070 0%, #ece890 100%)',
  'appliances-kitchen': 'linear-gradient(135deg, #909090 0%, #c0c0c0 100%)',
  outdoor: 'linear-gradient(135deg, #708868 0%, #98b090 100%)',
  'window-treatments': 'linear-gradient(135deg, #c0b8a8 0%, #dcd8cc 100%)',
  'rugs-soft': 'linear-gradient(135deg, #b09088 0%, #d0b8b0 100%)',
  acoustical: 'linear-gradient(135deg, #989088 0%, #c0b8b0 100%)',
  'electrical-accessories': 'linear-gradient(135deg, #888888 0%, #b0b0b0 100%)',
  'decor-accessories': 'linear-gradient(135deg, #c8a880 0%, #e0c8a8 100%)',
  // Architectural
  'ceiling-systems': 'linear-gradient(135deg, #d0d0d0 0%, #e8e8e8 100%)',
  'partition-wall': 'linear-gradient(135deg, #b8c0c8 0%, #d8dde0 100%)',
  'facade-envelope': 'linear-gradient(135deg, #788898 0%, #a0b0c0 100%)',
  'doors-openings': 'linear-gradient(135deg, #906848 0%, #b89070 100%)',
  'door-cabinet-hardware': 'linear-gradient(135deg, #909898 0%, #b8c0c0 100%)',
  'paving-decking': 'linear-gradient(135deg, #a09080 0%, #c8b8a8 100%)',
  'moulding-trim': 'linear-gradient(135deg, #c8c0b0 0%, #e0dcd0 100%)',
};

function humanize(str) {
  return String(str)
    .split('-')
    .map(w => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

function getOptionLabel(field, optionValue) {
  const options = field.enumOptions || field.filterConfig?.options || [];
  const found = options.find(o => o.option === optionValue);
  return found?.label || humanize(optionValue);
}

// Labels for the search-only keys (SEARCH_ONLY_PARAMS in SearchPage.shared.js):
// registered + queryable, but deliberately NOT listingFields, so they have no
// sidebar UI of their own. Without a pill each they would filter the grid
// INVISIBLY, with nothing to explain or undo them.
const SEARCH_ONLY_PILLS = {
  pub_colour_vibrancy: () => 'Colour: Colourful',
  pub_visual_effect: v => `Looks like: ${humanize(String(v).replace('has_any:', '').split(',')[0].replace(/-look$/, ''))}`,
  pub_mood: v => `Style: ${humanize(String(v).replace('has_any:', '').split(',')[0])}`,
};

function buildFilterPills(validQueryParams, listingFields, rawParams = {}) {
  const pills = [];
  for (const [key, label] of Object.entries(SEARCH_ONLY_PILLS)) {
    if (rawParams[key]) pills.push({ key, label: label(rawParams[key]), raw: true });
  }
  const skip = new Set([
    'pub_categoryLevel1',
    'pub_categoryLevel2',
    'pub_categoryLevel3',
    'sort',
    'page',
    'bounds',
    'origin',
    'mapSearch',
    'address',
    'keywords',
  ]);

  for (const [paramKey, paramValue] of Object.entries(validQueryParams)) {
    if (!paramValue || skip.has(paramKey)) continue;

    // Colour — special case
    if (paramKey === 'pub_colour_family') {
      const keys = String(paramValue)
        .replace('has_any:', '')
        .split(',');
      // A wide chromatic set (the "colourful / vibrant" query expansion) is
      // one intent, not 15 — label it as such instead of by its first slug.
      const label =
        keys.length >= 10
          ? 'Colour: Colourful'
          : keys.length > 1
          ? `Colour: ${humanize(keys[0])} +${keys.length - 1}`
          : `Colour: ${humanize(keys[0])}`;
      pills.push({ key: paramKey, label });
      continue;
    }

    // Look up field from listing config
    const field = (listingFields || []).find(
      f => `pub_${f.key}` === paramKey || f.key === paramKey
    );
    if (!field) continue;

    const fieldLabel = field.filterConfig?.label || humanize(field.key);
    const rawVal = String(paramValue);
    const valueKeys = rawVal.startsWith('has_any:')
      ? rawVal.replace('has_any:', '').split(',')
      : [rawVal];

    const firstLabel = getOptionLabel(field, valueKeys[0]);
    const extra = valueKeys.length - 1;
    const displayLabel =
      extra > 0 ? `${fieldLabel}: ${firstLabel} +${extra}` : `${fieldLabel}: ${firstLabel}`;

    pills.push({ key: paramKey, label: displayLabel });
  }

  return pills;
}

const SearchSubNav = props => {
  const {
    validQueryParams,
    config,
    totalItems,
    listingsAreLoaded,
    routeConfiguration,
    history,
  } = props;

  const listingFields = config?.listing?.listingFields || [];
  const activeCategoryL1 = validQueryParams['pub_categoryLevel1'];
  const activeCategoryL2 = validQueryParams['pub_categoryLevel2'];
  const activeCategoryL3 = validQueryParams['pub_categoryLevel3'];

  const topCats = config?.categoryConfiguration?.categories || [];
  const activeCat = topCats.find(c => c.id === activeCategoryL1);
  const level2Cats = activeCat?.subcategories || [];
  const activeL2Cat = level2Cats.find(s => s.id === activeCategoryL2);
  const level3Cats = activeL2Cat?.subcategories || [];
  const showLevel3 = !!activeCategoryL2 && level3Cats.length > 0;
  // Tile row now renders whichever level the user is currently browsing:
  //  · no L1 picked         → the 3 top-level categories (L1) — visual parity
  //                           with the L2/L3 tile design below.
  //  · L1 picked, no L3     → L2 subcategories
  //  · L1 + L2 picked       → L3 sub-subcategories
  // Click dispatch matches the current tile level.
  const showLevel1 = !activeCategoryL1;
  const tileCats = showLevel1 ? topCats : showLevel3 ? level3Cats : level2Cats;
  const activeInTiles = showLevel1
    ? activeCategoryL1
    : showLevel3
    ? activeCategoryL3
    : activeCategoryL2;

  const filterPills = buildFilterPills(validQueryParams, listingFields, validQueryParams);
  const hasActivePills = filterPills.length > 0;

  // Hide tab bar on scroll-down when tiles are visible; always show when no tiles
  const [tabBarHidden, setTabBarHidden] = useState(false);
  const hasTiles = tileCats.length > 0;

  // Chevron visibility for collapsed horizontal scroll
  const tileScrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateChevrons = useCallback(() => {
    const el = tileScrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 1);
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 1);
  }, []);

  // Recheck chevrons whenever collapsed state changes (after CSS transition settles)
  useEffect(() => {
    if (tabBarHidden && hasTiles) {
      const t = setTimeout(updateChevrons, 300);
      return () => clearTimeout(t);
    }
  }, [tabBarHidden, hasTiles, updateChevrons]);

  const handleTileScrollLeft = () => {
    tileScrollRef.current?.scrollBy({ left: -320, behavior: 'smooth' });
  };
  const handleTileScrollRight = () => {
    tileScrollRef.current?.scrollBy({ left: 320, behavior: 'smooth' });
  };

  const isCollapsed = tabBarHidden && hasTiles;

  useEffect(() => {
    if (!hasTiles) {
      setTabBarHidden(false);
      return;
    }
    const HIDE_THRESHOLD = 120; // collapse after scrolling past this
    const SHOW_THRESHOLD = 60; // restore only when scrolled back near top
    const onScroll = () => {
      const y = window.scrollY;
      if (y > HIDE_THRESHOLD) {
        setTabBarHidden(true);
      } else if (y < SHOW_THRESHOLD) {
        setTabBarHidden(false);
      }
      // between 60–120: keep current state (hysteresis zone)
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [hasTiles]);

  const navigate = params => {
    // Note: the AI THEME label (ai_query) is deliberately NOT carried through
    // any subnav navigation. validQueryParams comes from pickSearchParamsOnly,
    // which strips non-filter params, so every push from here — category
    // change, pill clear, clear-all — drops ai_query and ends the theme
    // (user decision 2026-08-10). The theme's colour filter itself follows
    // normal filter rules: pill-clears keep it unless it IS the cleared pill;
    // category changes reset it with everything else.
    history.push(createResourceLocatorString('SearchPage', routeConfiguration, {}, params));
  };

  // A category change is a CONTEXT switch, not a refinement — filters picked
  // in the old category rarely make sense in the new one (big-marketplace
  // convention: Amazon keeps the keyword and resets refinements on a
  // department change; Wayfair/Houzz treat a nav click as a fresh browse).
  // So every L1/L2/L3 change starts from this clean base instead of
  // spreading validQueryParams. Kept on purpose:
  //  - keywords: the search query belongs to the topbar, not the filter rail.
  //    (Note: pickSearchParamsOnly includes keywords only when the keywords
  //    filter is registered in the marketplace config — otherwise a tab
  //    click exits keyword search, same as before this reset existed.)
  //  - sort:     display preference (same rule as handleClearFilters)
  // Everything else resets — including an active AI theme (ai_query +
  // pub_colour_family). Switching category is a fresh browse; the theme
  // banner dies with its colour filter (user decision 2026-08-10).
  const baseParamsForCategoryChange = () => {
    const keep = {};
    if (validQueryParams['keywords']) keep['keywords'] = validQueryParams['keywords'];
    if (validQueryParams['sort']) keep['sort'] = validQueryParams['sort'];
    return keep;
  };

  // Behaviour warehouse: a category change is the heaviest "filter" there is
  // (it resets everything else), so each one logs the destination trio.
  const logCategoryChange = next =>
    recordBehaviorEvent('filter_change', {
      context: {
        changed: {
          category:
            [next.pub_categoryLevel1, next.pub_categoryLevel2, next.pub_categoryLevel3]
              .filter(Boolean)
              .join('>') || 'all',
        },
      },
    });

  const handleTopCategoryClick = catId => {
    const next = baseParamsForCategoryChange();
    if (activeCategoryL1 !== catId) {
      next['pub_categoryLevel1'] = catId;
    }
    logCategoryChange(next);
    navigate(next);
  };

  const handleSubCategoryClick = subId => {
    const next = baseParamsForCategoryChange();
    if (activeCategoryL1) next['pub_categoryLevel1'] = activeCategoryL1;
    if (activeCategoryL2 !== subId) {
      next['pub_categoryLevel2'] = subId;
    }
    logCategoryChange(next);
    navigate(next);
  };

  const handleLevel3Click = subId => {
    const next = baseParamsForCategoryChange();
    if (activeCategoryL1) next['pub_categoryLevel1'] = activeCategoryL1;
    if (activeCategoryL2) next['pub_categoryLevel2'] = activeCategoryL2;
    if (activeCategoryL3 !== subId) {
      next['pub_categoryLevel3'] = subId;
    }
    logCategoryChange(next);
    navigate(next);
  };

  const handleBack = () => {
    const next = baseParamsForCategoryChange();
    if (activeCategoryL1) next['pub_categoryLevel1'] = activeCategoryL1;
    navigate(next);
  };

  const handleClearPill = paramKey => {
    // validQueryParams now carries the search-only keys too (see
    // SEARCH_ONLY_PARAMS in SearchPage.shared.js), so clearing one pill no
    // longer silently clears its siblings.
    const next = { ...validQueryParams };
    delete next[paramKey];
    recordBehaviorEvent('filter_change', { context: { changed: { [paramKey]: null } } });
    navigate(next);
  };

  const handleClearFilters = () => {
    recordBehaviorEvent('filter_change', { context: { changed: { clear_filters: true } } });
    const keep = {};
    if (activeCategoryL1) keep['pub_categoryLevel1'] = activeCategoryL1;
    if (activeCategoryL2) keep['pub_categoryLevel2'] = activeCategoryL2;
    // L3 is navigation context like L1/L2 — clearing filters keeps the
    // user where they are in the category tree.
    if (activeCategoryL3) keep['pub_categoryLevel3'] = activeCategoryL3;
    if (validQueryParams['sort']) keep['sort'] = validQueryParams['sort'];
    navigate(keep);
  };

  const isCategorySelected = !!activeCategoryL1;

  return (
    <div
      className={classNames(css.root, {
        // Main /s (no L1 picked): no tab bar to hide into, so slide
        // the whole tile row out. Sub /s (L1 picked): slide by tab-bar
        // height so tiles fill the tab bar slot — existing behaviour.
        [css.rootHiddenFull]: tabBarHidden && hasTiles && showLevel1,
        [css.rootHidden]: tabBarHidden && hasTiles && !showLevel1,
      })}
    >
      {/* ── Tab bar (sticky) — tabs left, pills + count right ───────
          Tabs only render once a L1 has been picked (they act as a
          compact breadcrumb / quick-jump between L1s). Before any L1
          selection, the tile row below shows the 3 L1s as full tiles
          and the tab bar is hidden — the tiles ARE the picker. The
          whole bar is skipped when there's nothing to show (no L1 and
          no filter pills) so we don't render an empty white strip. */}
      {(isCategorySelected || hasActivePills) && (
        <div className={css.tabBar}>
          {isCategorySelected && (
            <div className={css.tabs}>
              {TOP_CATEGORIES.map(cat => (
                <button
                  key={cat.id}
                  type="button"
                  className={classNames(css.tab, {
                    [css.tabActive]: activeCategoryL1 === cat.id,
                  })}
                  onClick={() => handleTopCategoryClick(cat.id)}
                >
                  <span>{cat.label}</span>
                </button>
              ))}
            </div>
          )}
          <div className={css.tabBarRight}>
            {filterPills.map(pill => (
              <button
                key={pill.key}
                type="button"
                className={css.pill}
                onClick={() => handleClearPill(pill.key)}
              >
                {pill.label}
                <span className={css.pillX} aria-hidden="true">
                  ×
                </span>
              </button>
            ))}
            {hasActivePills && (
              <>
                <button type="button" className={css.clearAll} onClick={handleClearFilters}>
                  Clear
                </button>
                <span className={css.separator} />
              </>
            )}
            {/* Designs — a top-level destination, pulled to the RIGHT of the tab
                bar (away from the L1 category tabs) since it's a distinct content
                type. Tab form only when a category is open; the L1 picker shows
                it as a tile instead. Navigates away, so never carries tabActive. */}
            {DESIGN_SHOWCASE_ENABLED && isCategorySelected && (
              <button
                type="button"
                className={classNames(css.tab, css.designsTabRight)}
                onClick={() =>
                  history.push(
                    createResourceLocatorString('DesignBrowsePage', routeConfiguration, {}, {})
                  )
                }
              >
                <svg
                  className={css.designsSparkle}
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  aria-hidden="true"
                >
                  <path d="M12 2.5l1.35 6.35a3 3 0 0 0 2.3 2.3L22 12.5l-6.35 1.35a3 3 0 0 0-2.3 2.3L12 22.5l-1.35-6.35a3 3 0 0 0-2.3-2.3L2 12.5l6.35-1.35a3 3 0 0 0 2.3-2.3z" />
                </svg>
                <span>Showcase</span>
              </button>
            )}
            {/* What's New — the launch feed. Same treatment as Showcase: a
                top-level destination pulled to the RIGHT of the L1 category
                tabs, tab form only while a category is open (the L1 picker
                shows it as a tile below). Navigates away, so never tabActive. */}
            {isCategorySelected && SHOW_WHATS_NEW_NAV && (
              <button
                type="button"
                className={classNames(css.tab, css.whatsNewTabRight)}
                onClick={() =>
                  history.push(
                    createResourceLocatorString('WhatsNewPage', routeConfiguration, {}, {})
                  )
                }
              >
                <svg
                  className={css.designsSparkle}
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  aria-hidden="true"
                >
                  {/* Four-point star + a small companion — a sparkle that reads
                      as "new", distinct from the Showcase single star. */}
                  <path d="M10 2l1.15 4.85A3 3 0 0 0 14 9.15L18.5 10.3 14 11.45a3 3 0 0 0-2.85 2.3L10 18.6l-1.15-4.85a3 3 0 0 0-2.3-2.3L1.5 10.3 6.55 9.15a3 3 0 0 0 2.3-2.3z" />
                  <path d="M18 14l.5 2.1a1.4 1.4 0 0 0 1 1l2.1.5-2.1.5a1.4 1.4 0 0 0-1 1L18 21.2l-.5-2.1a1.4 1.4 0 0 0-1-1l-2.1-.5 2.1-.5a1.4 1.4 0 0 0 1-1z" />
                </svg>
                <span>
                  <FormattedMessage id="SearchSubNav.whatsNew" />
                </span>
              </button>
            )}
          </div>
        </div>
      )}

      {/* ── Tile row — L2, or L3 with back button inline ─────────── */}
      {tileCats.length > 0 && (
        <div className={css.tileRow}>
          {isCollapsed && canScrollLeft && (
            <button
              type="button"
              className={classNames(css.chevron, css.chevronLeft)}
              onClick={handleTileScrollLeft}
              aria-label="Scroll left"
            >
              <svg width="11" height="15" viewBox="0 0 11 15" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M9.4 1c.17 0 .34.065.458.192.214.228.182.57-.07.764L2.528 7.5l7.26 5.545c.252.194.284.535.07.763-.214.23-.592.257-.846.064l-7.8-5.958C1.077 7.81 1 7.66 1 7.5c0-.16.077-.31.212-.414l7.8-5.958C9.125 1.042 9.262 1 9.4 1"
                  fillRule="evenodd"
                />
              </svg>
            </button>
          )}
          {isCollapsed && canScrollRight && (
            <button
              type="button"
              className={classNames(css.chevron, css.chevronRight)}
              onClick={handleTileScrollRight}
              aria-label="Scroll right"
            >
              <svg width="11" height="15" viewBox="0 0 11 15" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M1.6 14c-.17 0-.34-.065-.458-.192-.214-.228-.182-.57.07-.764L8.472 7.5 1.21 1.955c-.252-.194-.284-.535-.07-.763.214-.23.592-.257.846-.064l7.8 5.958c.135.104.212.255.212.414 0 .16-.077.31-.212.414l-7.8 5.958c-.113.086-.25.128-.388.128"
                  fillRule="evenodd"
                />
              </svg>
            </button>
          )}
          <div
            ref={tileScrollRef}
            className={classNames(css.tileScroll, { [css.tileScrollCollapsed]: isCollapsed })}
            onScroll={isCollapsed ? updateChevrons : undefined}
          >
            {showLevel3 && (
              <button type="button" className={css.backButton} onClick={handleBack}>
                ← {activeL2Cat?.name}
              </button>
            )}
            {tileCats.map(sub => (
              <button
                key={sub.id}
                type="button"
                className={classNames(css.tile, { [css.tileActive]: activeInTiles === sub.id })}
                onClick={() =>
                  showLevel1
                    ? handleTopCategoryClick(sub.id)
                    : showLevel3
                    ? handleLevel3Click(sub.id)
                    : handleSubCategoryClick(sub.id)
                }
              >
                <span
                  className={css.tileImage}
                  style={{
                    background: CATEGORY_COLORS[sub.id] || '#e0e0e0',
                    ...(CATEGORY_THUMBNAILS[sub.id]
                      ? {
                          backgroundImage: `url(${CATEGORY_THUMBNAILS[sub.id]})`,
                          backgroundSize: ICON_THUMBNAIL_IDS.has(sub.id) ? 'contain' : 'cover',
                          backgroundRepeat: 'no-repeat',
                          backgroundPosition: 'center',
                        }
                      : null),
                  }}
                />
                <span className={css.tileName}>{sub.name}</span>
              </button>
            ))}
            {/* Designs entry point — TILE form only for the true L1 picker
                (no L1 selected, tab bar hidden, L1s shown as tiles): here the
                Designs tile sits as a peer of the L1 tiles. Once an L1 is
                selected the tab bar appears and Designs renders as a peer TAB
                (see the tabs block above), so the tile is suppressed to avoid
                showing it twice. */}
            {DESIGN_SHOWCASE_ENABLED && showLevel1 && (
              <button
                type="button"
                className={classNames(css.tile, css.designsTile)}
                onClick={() =>
                  history.push(
                    createResourceLocatorString('DesignBrowsePage', routeConfiguration, {}, {})
                  )
                }
              >
                <span
                  className={css.tileImage}
                  style={{
                    backgroundImage: `url(${CATEGORY_THUMBNAILS['designs']})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                  }}
                />
                <span className={css.tileName}>Showcase</span>
              </button>
            )}
            {/* What's New — TILE form for the true L1 picker only, sitting as a
                peer of the L1 tiles next to Showcase. Suppressed once an L1 is
                selected, where it renders as a peer TAB instead. No curated
                thumbnail: the tile wears the brand sparkle on a flat panel
                rather than borrowing a material photo that would date the
                moment that launch is over. */}
            {showLevel1 && SHOW_WHATS_NEW_NAV && (
              <button
                type="button"
                className={classNames(css.tile, css.whatsNewTile)}
                onClick={() =>
                  history.push(
                    createResourceLocatorString('WhatsNewPage', routeConfiguration, {}, {})
                  )
                }
              >
                <span className={classNames(css.tileImage, css.whatsNewTileImage)}>
                  <svg
                    width="22"
                    height="22"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path d="M10 2l1.15 4.85A3 3 0 0 0 14 9.15L18.5 10.3 14 11.45a3 3 0 0 0-2.85 2.3L10 18.6l-1.15-4.85a3 3 0 0 0-2.3-2.3L1.5 10.3 6.55 9.15a3 3 0 0 0 2.3-2.3z" />
                    <path d="M18 14l.5 2.1a1.4 1.4 0 0 0 1 1l2.1.5-2.1.5a1.4 1.4 0 0 0-1 1L18 21.2l-.5-2.1a1.4 1.4 0 0 0-1-1l-2.1-.5 2.1-.5a1.4 1.4 0 0 0 1-1z" />
                  </svg>
                </span>
                <span className={css.tileName}>
                  <FormattedMessage id="SearchSubNav.whatsNew" />
                </span>
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchSubNav;
